import { Compass } from 'lucide-react';
import { cn } from '../../lib/utils';

export const RISK = {
  critical: { label: 'Critical', badge: 'bg-rose-100 text-rose-700 border-rose-200', dot: 'bg-rose-500', bar: 'bg-rose-500' },
  at_risk: { label: 'At Risk', badge: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', bar: 'bg-amber-500' },
  healthy: { label: 'Healthy', badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', bar: 'bg-emerald-500' },
};

export const covColor = (pct) => (pct >= 100 ? 'bg-emerald-500' : pct >= 75 ? 'bg-cyan-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500');

export function CoverageBar({ pct, className }) {
  return (
    <div className={cn('h-1.5 w-full rounded-full bg-slate-100 overflow-hidden', className)}>
      <div className={cn('h-full rounded-full transition-all', covColor(pct))} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith('**') && p.endsWith('**')
      ? <strong key={i} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
      : <span key={i}>{p}</span>);
}

export function BriefMarkdown({ text }) {
  if (!text) return null;
  const blocks = [];
  let list = [];
  const flush = (key) => {
    if (list.length) { blocks.push(<ul key={`ul-${key}`} className="list-disc pl-5 space-y-1 my-2">{list}</ul>); list = []; }
  };
  text.split('\n').forEach((raw, i) => {
    const line = raw.trim();
    if (!line) { flush(i); return; }
    if (line.startsWith('### ')) { flush(i); blocks.push(<h4 key={i} className="text-sm font-bold text-cyan-800 mt-4 mb-1 first:mt-0">{line.slice(4)}</h4>); return; }
    const li = line.match(/^(?:[-*]\s+|\d+\.\s+)(.*)$/);
    if (li) { list.push(<li key={i} className="text-sm text-slate-700 leading-relaxed">{renderInline(li[1])}</li>); return; }
    flush(i);
    blocks.push(<p key={i} className="text-sm text-slate-700 leading-relaxed my-1.5">{renderInline(line)}</p>);
  });
  flush('end');
  return <div data-testid="cap-brief-content">{blocks}</div>;
}

export function CohortTags({ cohorts, testId, max = 2 }) {
  if (!cohorts || cohorts.length === 0) return null;
  const shown = cohorts.slice(0, max);
  const extra = cohorts.length - shown.length;
  return (
    <span className="inline-flex items-center gap-1 flex-wrap" data-testid={testId}>
      {shown.map((c, i) => (
        <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap">{c}</span>
      ))}
      {extra > 0 && <span className="text-[9px] text-slate-400" title={cohorts.join(', ')}>+{extra}</span>}
    </span>
  );
}

export function RecChips({ items, testId }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid={testId}>
      <span className="text-[9px] text-slate-400 uppercase tracking-wide mr-0.5 flex items-center gap-0.5">
        <Compass className="h-3 w-3" /> Suggested
      </span>
      {items.map(i => (
        <span key={i.id} className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-200" title={i.category}>
          {i.name}
        </span>
      ))}
    </div>
  );
}
